import { useCallback, useEffect, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const API_BASE = `${API_URL}/v1/admin/api_tokens`;

export default function useApiTokensManagement() {
  const isMounted = useIsMounted();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(API_BASE);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load API tokens');
      }
      const json = await res.json();
      setTokens(Array.isArray(json) ? json : []);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err);
      setTokens([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    reload();
  }, [reload]);

  const createToken = useCallback(async ({ name, ownerId, role }) => {
    setActionError('');
    const res = await fetchWithAuth(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_token: {
          name,
          owner_id: ownerId,
          role,
        },
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = Array.isArray(data.errors) ? data.errors.join(', ') : (data.error || 'Failed to create API token');
      setActionError(message);
      throw new Error(message);
    }

    await reload();
    return data;
  }, [reload]);

  const revokeToken = useCallback(async (id) => {
    setActionError('');
    const res = await fetchWithAuth(`${API_BASE}/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const message = data.error || 'Failed to revoke API token';
      setActionError(message);
      throw new Error(message);
    }
    await reload();
  }, [reload]);

  return {
    tokens,
    loading,
    error,
    actionError,
    setActionError,
    reload,
    createToken,
    revokeToken,
  };
}
